import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    CredenzaBody,
    CredenzaDescription,
    CredenzaFooter,
    CredenzaHeader,
    CredenzaTitle,
} from "@/components/ui/credenza";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useScopedI18n } from "@/locales/client";
import { trpc } from "@/util/trpc";
import dayjs from "dayjs";
import { CalendarIcon, Tags } from "lucide-react";

export function Idle({
    claims,
    onClick,
}: { claims: { type: "campaign" | "youtube"; id: string }[]; onClick: () => void }) {
    const t = useScopedI18n("paymentDialogs.campaignDialog.idle");
    const { data: payoutsSummary, isLoading } = trpc.edge.utils.getPayoutsSummary.useQuery({
        filter: claims,
    });

    return (
        <>
            <CredenzaHeader>
                <CredenzaTitle>{t("title")}</CredenzaTitle>
                <CredenzaDescription>{t("description", { count: claims.length })}</CredenzaDescription>
            </CredenzaHeader>

            <CredenzaBody className="space-y-4">
                <div className="max-h-[300px] overflow-y-auto">
                    {isLoading ? (
                        <>
                            {Array.from({ length: claims.length }).map((_, i) => (
                                <div className="flex w-full justify-between" key={`skeleton-${i}`}>
                                    <div className="">
                                        <div className="mb-1 font-bold text-sm">
                                            <Skeleton className="h-4 w-[150px]" />
                                        </div>
                                        <div className="flex items-center">
                                            <div className="text-muted-foreground text-xs">{t("details.type")}:</div>
                                            <Skeleton className="ml-1 h-4 w-[50px]" />
                                            <div className="ml-2 text-muted-foreground text-xs">
                                                {t("details.fee")}:
                                            </div>
                                            <Skeleton className="ml-1 h-4 w-[40px]" />
                                        </div>
                                        <div className="mt-2 flex items-center text-muted-foreground text-xs">
                                            <CalendarIcon className="mr-1 size-3" />
                                            {t("details.period")}
                                            <Skeleton className="ml-1 h-4 w-[100px]" />
                                        </div>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <Skeleton className="h-4 w-[80px]" />
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        payoutsSummary?.items.map((item) => (
                            <div className="flex w-full justify-between" key={`${item.type}-${item.payoutId}`}>
                                <div className="space-y-1">
                                    <div className="font-bold text-sm">{item.title}</div>
                                    <div className="flex items-center">
                                        <Badge className="px-2 text-xs" variant="secondary">
                                            {item.label}
                                        </Badge>
                                        <Tags className="ml-3 size-3 text-muted-foreground" />
                                        <div className="ml-1 text-muted-foreground text-xs">{t("details.fee")}</div>
                                        <Badge className="ml-1 px-2 text-xs" variant="outline">
                                            {item.fee}%
                                        </Badge>
                                    </div>
                                    {item.firstDay && item.lastDay && (
                                        <div className="flex items-center text-muted-foreground text-xs">
                                            <CalendarIcon className="mr-1 size-3" />
                                            {t("details.period")}
                                            <span className="ml-1 text-foreground">
                                                {dayjs(item.firstDay).format("MMM DD, YYYY")} -{" "}
                                                {dayjs(item.lastDay).format("MMM DD, YYYY")}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center text-sm">
                                    {(+item.amountWithFeeSubtracted).toLocaleString("en-US", {
                                        style: "currency",
                                        currency: item.payout.currency,
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <Separator />

                <div className="mb-4 flex justify-between">
                    <div className="text-muted-foreground text-sm">{t("details.total")}</div>
                    <div className="font-bold text-sm">
                        {isLoading ? (
                            <Skeleton className="h-4 w-[100px]" />
                        ) : payoutsSummary?.amountWithFeeSubtracted ? (
                            (+payoutsSummary?.amountWithFeeSubtracted).toLocaleString("en-US", {
                                style: "currency",
                                currency: payoutsSummary?.totalCurrency,
                            })
                        ) : (
                            "-"
                        )}
                    </div>
                </div>
            </CredenzaBody>

            <CredenzaFooter>
                <Button onClick={onClick}>{t("buttons.continue")}</Button>
            </CredenzaFooter>
        </>
    );
}
